import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { api, HiringNeedResponse, PeriodoNecesidad } from '../../../services/api'

const Section = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
`

const SectionTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.primary};
`

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
`

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
`

interface CardProps {
    bgGradient: string
}

const Card = styled.div<CardProps>`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  text-align: center;
  color: white;
  cursor: pointer;
  transition: transform ${({ theme }) => theme.transitions.normal},
              box-shadow ${({ theme }) => theme.transitions.normal};
  background: ${({ bgGradient }) => bgGradient};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
`

const CardValue = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xxl};
  font-weight: 700;
`

const CardLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-top: ${({ theme }) => theme.spacing.xs};
`

const CardSublabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  opacity: 0.85;
`

const AlertBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: linear-gradient(135deg, #FFEBEE, #FFCDD2);
  border: 1px solid #EF5350;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: #C62828;
`

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.textMuted};
`

interface HiringNeedsSectionProps {
    hiringNeeds?: {
        inminente: number
        corto: number
        medio: number
        largo: number
    }
    onCardClick?: (type: string, periodo: PeriodoNecesidad | null) => void
}

export const HiringNeedsSection: React.FC<HiringNeedsSectionProps> = ({
    hiringNeeds: legacyHiringNeeds,
    onCardClick,
}) => {
    const [loading, setLoading] = useState(true)
    const [response, setResponse] = useState<HiringNeedResponse | null>(null)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        fetchHiringNeeds()
    }, [])

    const fetchHiringNeeds = async () => {
        setLoading(true)
        try {
            const result = await api.recommendations.getHiringNeed({
                granularidad: 'mensual',
            })
            setResponse(result.data)
        } catch (err: any) {
            console.error('Error fetching hiring needs:', err)
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    // Calculate period buckets from API response
    const calculateNeeds = () => {
        if (!response?.periodos.length) {
            return legacyHiringNeeds || { inminente: 0, corto: 0, medio: 0, largo: 0 }
        }

        const now = new Date()
        let inminente = 0
        let corto = 0
        let medio = 0
        let largo = 0

        response.periodos.forEach((periodo, index) => {
            const deficit = periodo.deficit
            // Only count as need if deficit is significant (>= 0.5 persons)
            const roundedDeficit = deficit >= 0.5 ? Math.ceil(deficit) : 0
            if (index === 0) {
                inminente = roundedDeficit
            } else if (index === 1) {
                corto = roundedDeficit
            } else if (index >= 2 && index <= 4) {
                medio += deficit
            } else {
                largo += deficit
            }
        })

        return {
            inminente,
            corto,
            medio: medio >= 0.5 ? Math.ceil(medio) : 0,
            largo: largo >= 0.5 ? Math.ceil(largo) : 0,
        }
    }

    const handleCardClick = (type: string) => {
        let periodo: PeriodoNecesidad | null = null

        if (response?.periodos) {
            const index = type === 'inminente' ? 0 :
                type === 'corto' ? 1 :
                    type === 'medio' ? 2 :
                        response.periodos.length - 1

            if (response.periodos[index]) {
                periodo = response.periodos[index]
            }
        }

        if (onCardClick) {
            onCardClick(type, periodo)
        }
    }

    const needs = calculateNeeds()
    const hasAlert = response?.resumen?.alerta || false

    if (loading) {
        return (
            <Section>
                <SectionTitle>📋 Necesidad de Contratación</SectionTitle>
                <LoadingSpinner>Cargando predicción...</LoadingSpinner>
            </Section>
        )
    }

    return (
        <Section>
            <SectionTitle>📋 Necesidad de Contratación</SectionTitle>
            <Subtitle>
                Basado en la carga proyectada del equipo
                {response?.resumen?.num_ofertas_analizadas &&
                    ` (${response.resumen.num_ofertas_analizadas} ofertas analizadas)`
                }
            </Subtitle>

            {hasAlert && (
                <AlertBanner>
                    ⚠️ Alerta: Se detecta una necesidad de contratación superior al umbral configurado
                </AlertBanner>
            )}

            <CardsGrid>
                <Card
                    bgGradient="linear-gradient(135deg, #C62828, #E53935)"
                    onClick={() => handleCardClick('inminente')}
                >
                    <CardValue>{needs.inminente}</CardValue>
                    <CardLabel>Inminente</CardLabel>
                    <CardSublabel>Ya / Ahora</CardSublabel>
                </Card>
                <Card
                    bgGradient="linear-gradient(135deg, #EF6C00, #FF9800)"
                    onClick={() => handleCardClick('corto')}
                >
                    <CardValue>{needs.corto}</CardValue>
                    <CardLabel>Corto Plazo</CardLabel>
                    <CardSublabel>1 mes</CardSublabel>
                </Card>
                <Card
                    bgGradient="linear-gradient(135deg, #1565C0, #1E88E5)"
                    onClick={() => handleCardClick('medio')}
                >
                    <CardValue>{needs.medio}</CardValue>
                    <CardLabel>Medio Plazo</CardLabel>
                    <CardSublabel>2-4 meses</CardSublabel>
                </Card>
                <Card
                    bgGradient="linear-gradient(135deg, #2E7D32, #43A047)"
                    onClick={() => handleCardClick('largo')}
                >
                    <CardValue>{needs.largo}</CardValue>
                    <CardLabel>Largo Plazo</CardLabel>
                    <CardSublabel>+6 meses</CardSublabel>
                </Card>
            </CardsGrid>
        </Section>
    )
}
